/**
 * Le découpage d'un message en texte et en liens — adresse web, téléphone, e-mail.
 *
 * C'est celui de l'ancien fil : `<GiftedChat>` passait chaque message à `react-native-parsed-text`
 * avec ses trois motifs, dans cet ordre, et ce sont SES expressions qui sont reprises ici, à la
 * lettre (`ParsedText.PATTERNS`). Comme lui, chaque motif ne cherche que dans ce que les motifs
 * précédents ont laissé : une adresse web qui contient des chiffres ne devient pas un numéro, ni un
 * e-mail. Pur, sans dépendance : la bulle le lit, ses tests le rejouent.
 */
import { type UIMessageLink, type UIMessageLinkKind } from "../../../ports/primary/ui/types/studentConversation"

export type { UIMessageLink, UIMessageLinkKind }

/** Un morceau du message : du texte, ou un lien qui se touche. */
export type MessageSegment = { text: string; link?: UIMessageLink }

/** `react-native-parsed-text`, `src/ParsedText.js` — mêmes expressions, même ordre. */
const PATTERNS: { kind: UIMessageLinkKind; pattern: RegExp }[] = [
    {
        kind: "url",
        pattern:
            /(https?:\/\/|www\.)[-a-zA-Z0-9@:%._+~#=]{1,256}\.(xn--)?[a-z0-9-]{2,20}\b([-a-zA-Z0-9@:%_+[\],.~#?&/=]*[-a-zA-Z0-9@:%_+\]~#?&/=])*/i,
    },
    { kind: "phone", pattern: /[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,7}/ },
    { kind: "email", pattern: /\S+@\S+\.\S+/ },
]

/** Un motif, sur les morceaux encore libres : chaque trouvaille devient un lien, le reste reste à chercher. */
function splitBy(segments: MessageSegment[], kind: UIMessageLinkKind, pattern: RegExp): MessageSegment[] {
    const out: MessageSegment[] = []
    for (const segment of segments) {
        if (segment.link != null) {
            out.push(segment)
            continue
        }
        let rest = segment.text
        let match = pattern.exec(rest)
        while (rest !== "" && match != null) {
            if (match.index > 0) out.push({ text: rest.slice(0, match.index) })
            out.push({ text: match[0], link: { kind, value: match[0] } })
            rest = rest.slice(match.index + match[0].length)
            match = pattern.exec(rest)
        }
        if (rest !== "") out.push({ text: rest })
    }
    return out
}

/** Le message en morceaux, dans l'ordre. Sans lien : un seul morceau, le texte entier. */
export function messageSegments(text: string): MessageSegment[] {
    if (text === "") return []
    return PATTERNS.reduce<MessageSegment[]>(
        (segments, { kind, pattern }) => splitBy(segments, kind, pattern),
        [{ text }]
    )
}
